
import { Vue, Options } from "vue-class-component";
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import PreviewImagePod from "../../shipment/tracking-stt-cargo/tracking-stt-tab/component/preview-image-pod.vue";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import OverlayPanel from "primevue/overlaypanel";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { CargoController } from "@/app/ui/controllers/CargoController";
import {
  CargoV2Data,
  BagOrSttDetail
} from "@/domain/entities/CargoV2";
import CardFlightDetail from "./card-flight-detail.vue";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import { AccountController } from "@/app/ui/controllers/AccountController";

interface PrintStatusItem {
  label: string;
  printed: boolean;
}

class Props {}

@Options({
  components: {
    PreviewImagePod,
    OverlayPanel,
    CardFlightDetail
  }
})
class CargoV2DetailInfo extends Vue.with(Props) {
  isPreviewPhotoProof = false;
  photoProofItems: string[] = [];

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }
  pagination = {
    page: 1,
    limit: 100
  };

  get detailData() {
    if (this.$route?.query?.rc_no) {
      return CargoController.cargoDetailData;
    } else {
      return CargoV2Controller.cargoDetailData;
    }
  }

  get detailTrackingStatus() {
    return (this.detailData as any)?.trackingStatus || "";
  }

  get isInternal() {
    return AccountController.accountData.account_type === "internal";
  }

  get dataTable() {
    return CargoV2Controller.cargoDetailDataTable;
  }

  get bookingDate() {
    return formatDateBasedTimezoneValue(this.detailData.createdAt);
  }

  get updateDate() {
    return formatDateBasedTimezoneValue(this.detailData.updatedAt);
  }

  get listProofImageData() {
    return this.detailData?.photoProof.slice(0, 2);
  }

  get isInProcess() {
    return this.detailData.cargoStatus.includes("process");
  }

  get printStatus() {
    const data = CargoV2Controller.cargoDetailData;

    return this.getPrintStatusItems(data);
  }

  get disputeStatus() {
    const data = this.detailData;

    return [
      {
        isPrinted: data.isDisputeFromBagging,
        printerName: this.$t("cargo.v2.detail.disputeStatus.bagging")
      },
      {
        isPrinted: data.isDisputeFromCargo,
        printerName: this.$t("cargo.v2.detail.disputeStatus.cargo")
      }
    ];
  }

  getPrintStatusItems(item: CargoV2Data): PrintStatusItem[] {
    const isPlane = item.cargoType === "plane";
    const isBTH =
      item.originCityCode === "BTH" || item.destinationCityCode === "BTH";

    const label = {
      label: this.$t("cargo.v2.detail.section.printLabel"),
      printed: item.isPrintLabel
    };
    const awb = { label: "AWB Lion Cargo", printed: item.isPrintAwb };
    const pti = { label: "PTI", printed: item.isPrintPti };
    const sptj = { label: "SPTJ", printed: item.isPrintSptj };
    const manifest = {
      label: this.$t("cargo.v2.detail.section.printManifest"),
      printed: item.isPrintManifest
    };

    if (!isPlane) return [label, manifest];

    if (isBTH) return [label, awb, manifest];

    const items: PrintStatusItem[] = [label, awb];

    if (FlagsPermissionCargo.permission_cargo_print_pti_pdf.isEnabled()) {
      items.push(pti);
    }

    if (
      (FlagsPermissionCargo.permission_cargo_print_electronic_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsElectronicDg) ||
      (FlagsPermissionCargo.permission_cargo_print_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsDg &&
        !item.cargoCommodityGroupIsElectronicDg)
    ) {
      items.push(sptj);
    }

    items.push(manifest);

    return items;
  }

  get columns() {
    return [
      {
        name: this.$t("cargoV2.components.scanTableList.column.no"),
        styleHead: "w-10 text-center whitespace-no-wrap",
        styleCustom: "align-center",
        render: (_: any, index: number) => {
          return `<div class="text-black-lp-300">${index + 1}</div>`;
        }
      },
      {
        name: this.$t("cargo.labels.noBag"),
        styleHead: "w-56 text-left whitespace-nowrap text-black-lp-300",
        "custom-slot": true,
        slotName: "no_bag"
      },
      {
        name: this.$t("cargoV2.components.scanTableList.column.chargeableBag").replace(" ", "<br/>"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: BagOrSttDetail) => {
          const weight =
            item.chargeableWeight ||
            item.bagData?.bagTotalChargeableWeight ||
            0;
          return `<div class="text-black-lp-300 whitespace-no-wrap">
              ${weight ? `${this.decimalNumberColumnData(weight)}` : "-"}
            </div>`;
        }
      },
      {
        name: this.$t("cargo.labels.noStt"),
        styleHead: "w-44 text-left whitespace-no-wrap",
        render: (item: BagOrSttDetail) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const detail of item.sttDetails) {
              const dgBadge = detail.isDangerousGoods
                ? `<span class="bg-red-100 text-red-600 text-[10px] px-1 py-0.5 rounded border border-red-200 font-bold inline-block ml-1">DG</span>`
                : "";
              const KBadge = detail.isQuarantine
                ? `<span
                class="bg-yellow-lp-200 rounded text-center text-yellow-lp-100 font-semibold text-xs p-1"
              >
                K
              </span>`
                : "";
              const shipmentId =
                detail.shipmentId && detail.shipmentId !== "-"
                  ? `<div class="text-14px text-gray-lp-800  font-normal whitespace-normal break-all truncate">
                     ${item.shipmentId || "-"}
                    </div>`
                  : "";

              html += `
                <div class="min-w-0">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-black-lp-300 whitespace-normal break-all font-medium">${detail.sttNo ||
                      "-"}</span>
                    ${dgBadge}
                    ${KBadge}
                  </div>
                  ${shipmentId}
                </div>`;
            }
            html += `</div>`;
            return html;
          }

          return "";
        }
      },
      {
        name: this.$t("cargoV2.components.scanTableList.column.chargeableStt").replace(" ", " <br />"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: BagOrSttDetail) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const detail of item.sttDetails) {
              html += `<div class="text-black-lp-300">${
                detail.sttChargeableWeight
                  ? `${this.decimalNumberColumnData(
                      detail.sttChargeableWeight
                    )}`
                  : "-"
              }</div>`;
            }
            html += `</div>`;
            return html;
          }
          return "";
        }
      },
      {
        name: this.$t("cargo.labels.shipmentType"),
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: BagOrSttDetail) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const detail of item.sttDetails) {
              html += `<div><span class="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">${detail.productType ||
                "-"}</span></div>`;
            }
            html += `</div>`;
            return html;
          }
          return "";
        }
      },
      {
        name: this.$t("cargo.v2.detail.field.commodity"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: BagOrSttDetail) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const detail of item.sttDetails) {
              html += `<div class="text-black-lp-300">${this.elipsisText(
                detail.commodityName || "-"
              )}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return "";
        }
      },
      {
        name: this.$t("cargo.labels.destinationCity"),
        styleHead: "w-32 text-center whitespace-no-wrap",
        styleCustom: "text-center",
        render: (item: BagOrSttDetail) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const detail of item.sttDetails) {
              html += `<div><span class="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">${detail.destinationCityCode ||
                "-"}</span></div>`;
            }
            html += `</div>`;
            return html;
          }
          return "";
        }
      }
    ];
  }

  get isSttEmpty() {
    return !this.detailData?.detailStt?.length;
  }

  get totalDisputeBagging() {
    return this.detailData.totalDisputeBagging;
  }

  elipsisText(val: string) {
    return ellipsisString(val, 13);
  }

  convertDecimalWithComma(value: any) {
    return formatByCountry(Number(value), { type: "weight", country: this.countryCode });
  }

  decimalNumberColumnData(number: number | string) {
    const decimalNumber = Number(number);
    return decimalNumber !== 0 ? `${formatByCountry(decimalNumber, { type: "weight", country: this.countryCode })} kg` : "0 kg";
  }

  onOpenPreviewPhotoProof(photos: string[]) {
    this.isPreviewPhotoProof = true;
    this.photoProofItems = photos;
  }

  onClosePreviewPhotoProof() {
    this.isPreviewPhotoProof = false;
    this.photoProofItems = [];
  }
}

export default CargoV2DetailInfo;
